import type { SkillCommit } from '../../lib/api-client';

interface Props {
  commits: SkillCommit[];
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
  hasMore: boolean;
  onLoadMore: () => void;
  /** null = viewing HEAD; otherwise the active commit hash. */
  viewedRef: string | null;
  /** Click a row to time-travel; pass null to return to HEAD. */
  onViewRef: (ref: string | null) => void;
}

function relativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const sec = Math.round((now - then) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    if (sec < 30 * 86400) return `${Math.round(sec / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function SkillRevisionList({
  commits,
  status,
  error,
  hasMore,
  onLoadMore,
  viewedRef,
  onViewRef,
}: Props) {
  return (
    <div style={{ background: 'var(--surface)' }}>
      <div
        style={{
          padding: '12px 20px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span>Revision history</span>
        {viewedRef && (
          <button
            type="button"
            onClick={() => onViewRef(null)}
            style={{
              padding: '3px 8px',
              fontSize: 11,
              border: '1px solid var(--accent)',
              borderRadius: 5,
              background: 'transparent',
              color: 'var(--accent)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textTransform: 'none',
              letterSpacing: 0,
              fontWeight: 500,
            }}
          >
            ← Back to HEAD
          </button>
        )}
      </div>

      {status === 'loading' && commits.length === 0 && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading history…</div>
      )}
      {status === 'error' && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
          {error ?? 'Failed to load history.'}
        </div>
      )}
      {status === 'success' && commits.length === 0 && (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>
          No commits touched this skill yet.
        </div>
      )}

      {commits.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
            }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-alt)' }}>
                <Th>Hash</Th>
                <Th>Subject</Th>
                <Th>Author</Th>
                <Th>When</Th>
                <Th>Files</Th>
                <Th>±</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {commits.map((commit, idx) => {
                const isViewed = viewedRef === commit.hash;
                const isActiveHead = viewedRef === null && idx === 0;
                return (
                  <tr
                    key={commit.hash}
                    style={{
                      background: isViewed ? 'var(--bg-alt2, var(--bg-alt))' : 'transparent',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <Td mono>{commit.short_hash}</Td>
                    <Td>
                      {commit.subject}
                      {isActiveHead && (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: '1px 6px',
                            fontSize: 10,
                            color: 'var(--accent)',
                            border: '1px solid var(--accent)',
                            borderRadius: 4,
                          }}
                        >
                          HEAD
                        </span>
                      )}
                    </Td>
                    <Td muted>{commit.author}</Td>
                    <Td muted>{relativeTime(commit.date)}</Td>
                    <Td muted>{commit.files_changed}</Td>
                    <Td mono>
                      {commit.additions > 0 && (
                        <span style={{ color: 'var(--kind-success, #2da44e)' }}>+{commit.additions}</span>
                      )}
                      {commit.additions > 0 && commit.deletions > 0 && ' '}
                      {commit.deletions > 0 && (
                        <span style={{ color: 'var(--kind-failure, #cf222e)' }}>-{commit.deletions}</span>
                      )}
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => onViewRef(isViewed ? null : commit.hash)}
                        style={{
                          padding: '3px 10px',
                          fontSize: 11,
                          border: '1px solid var(--border-strong)',
                          borderRadius: 5,
                          background: isViewed ? 'var(--accent)' : 'transparent',
                          color: isViewed ? '#fff' : 'var(--text)',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        {isViewed ? 'Viewing' : 'View'}
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            onClick={onLoadMore}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              background: 'var(--surface)',
              color: 'var(--text)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th
      style={{
        padding: '8px 12px',
        textAlign: 'left',
        fontWeight: 500,
        fontSize: 11,
        color: 'var(--text-mute)',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  muted,
}: {
  children?: React.ReactNode;
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      style={{
        padding: '8px 12px',
        color: muted ? 'var(--text-mute)' : 'var(--text)',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </td>
  );
}
